import { useState } from 'react';
import Swal from 'sweetalert2';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import { fetchStaffList, massiveStaffUploadService, postStaff, removeStaffAssignment, toggleStaffStatus } from '../services/staff.service';

/**
 * Custom hook para manejar la lista de staff de un supplier usando React Query
 * @param {Object} params - Parámetros del hook
 * @param {Object} params.supplier - Objeto del supplier
 * @returns {Object} - Resultado de la query con data, loading, error, etc.
 */

export const useStaffList = ({ 
    supplier, 
}) => {
    const queryClient = useQueryClient();
    
    // ESTADO PARA EL FILTRO DE MIEMBROS INACTIVOS
    const [showInactiveOnly, setShowInactiveOnly] = useState(false);

	// ESTADO PARA LA ENTIDAD SELECCIONADA
    const [entitySelected, setEntitySelected] = useState(null);
    
    const queryKey = [
        'staffList', 
        supplier?._id, 
        showInactiveOnly, 
        entitySelected?._id
    ];

    const {
        data,
        isError,
        error,
        refetch,
    } = useQuery({
        queryKey,
        queryFn: () => {
            const supplierId = supplier._id;
            const query = new URLSearchParams({
                showInactive: showInactiveOnly,
                entityIdSelected: entitySelected._id || "",
            });

            return fetchStaffList({ supplierId, query });
        },
        enabled: !!supplier && !!entitySelected,
        staleTime: 0, // Los datos se consideran obsoletos inmediatamente
        cacheTime: 0, // No mantener cache, fetch siempre que se monte
        retry: 0,
        retryDelay: (attemptIndex) => Math.min(1000 * 2 ** attemptIndex, 30000),
        refetchOnMount: true, // Siempre refetch al montar el componente
        refetchOnWindowFocus: false, // No refetch al enfocar la ventana
        refetchOnReconnect: true, // Refetch al reconectarse
        onError: (error) => {
            console.error('Error fetching staff list:', error.response?.data?.message || error.message || 'Error getting staff list');
        },
    });

    // mutations
    const massiveStaffUploadMutation = useMutation({
        mutationFn: ({ supplierId, entityId, file }) => 
            massiveStaffUploadService({ supplierId, entityId, file }),
        onMutate: () => {
            // Mostrar loading antes de iniciar
            Swal.fire({
                footer: 'Loading, please wait',
                showConfirmButton: false,
                allowOutsideClick: false,
                allowEscapeKey: false,
                didOpen: () => {
                    Swal.showLoading();
                },
            });
        },    
        onSuccess: () => {
            // Mostrar success y refetch
            Swal.fire({
                icon: 'success',
                text: 'Staff members uploaded successfully',
                showConfirmButton: false,
                timer: 5000,
                timerProgressBar: true,
            });
            
            // Invalidar las queries relacionadas para refrescar los datos
            queryClient.invalidateQueries(queryKey);
        },
        onError: (error) => {
            // Mostrar error solo después del último retry
            Swal.fire({
                icon: 'error',
                text: error.response?.data?.message || 'An error occurred while uploading the file.',
                showConfirmButton: false,
                timer: 5000,
                timerProgressBar: true,
            });
            
            console.error('Error uploading staff members:', error?.message || 'Upload failed');
        },
    })

    const createStaffMutation = useMutation({
        mutationFn: ({ supplierId, entityId, staffData }) => 
            postStaff({ supplierId, entityId, staffData }),
        onMutate: () => {
            // Mostrar loading antes de iniciar
            Swal.fire({
                footer: 'Loading, please wait',
                showConfirmButton: false,
                allowOutsideClick: false,
                allowEscapeKey: false,
                didOpen: () => {
                    Swal.showLoading();
                },
            });
        },    
        onSuccess: () => {
            // Mostrar success y refetch
            Swal.fire({
                icon: 'success',
                text: 'Staff member added successfully',
                showConfirmButton: false,
                timer: 5000,
                timerProgressBar: true,
            });
            
            // Invalidar las queries relacionadas para refrescar los datos
            queryClient.invalidateQueries(queryKey);
        },
        onError: (error) => {
            // Mostrar error solo después del último retry
            Swal.fire({
                icon: 'error',
                text: error.response?.data?.message || 'An error occurred while adding the staff member.',
                showConfirmButton: false,
                timer: 5000,
                timerProgressBar: true,
            });
            
            console.error('Error adding staff member:', error?.message || 'Add staff member failed');
        },
    })

    const toggleStaffStatusMutation = useMutation({
        mutationFn: ({ assignmentId }) => 
            toggleStaffStatus({ assignmentId }),
        onMutate: async ({ assignmentId }) => {
            await queryClient.cancelQueries(queryKey);
            
            // Do optimistic update
            const previousData = queryClient.getQueryData(queryKey);

            const findStaffAssignment = previousData.find(staff => staff.staffAssignmentId === assignmentId);

            if (!findStaffAssignment) return { previousData };

            findStaffAssignment.active = !findStaffAssignment.active;

            queryClient.setQueryData(queryKey, previousData);

            return { previousData };
        },
        onError: (error, variables, context) => {
            // regresar al estado anterior en caso de error
            if (context?.previousData) {
                queryClient.setQueryData(queryKey, context.previousData);
            }
            
            console.error('Error toggling staff status:', error.response?.data?.message || error.message);
        },
        onSettled: () => {
            // Invalidar las queries relacionadas para refrescar los datos
            queryClient.invalidateQueries({
                queryKey,
                refetchType: "none"
            });
        },
    })

    const removeStaffAssignmentMutation = useMutation({
        mutationFn: ({ assignmentId }) => 
            removeStaffAssignment({ assignmentId }),
        onMutate: () => {
            // Mostrar loading antes de iniciar
            Swal.fire({
                footer: 'Loading, please wait',
                showConfirmButton: false,
                allowOutsideClick: false,
                allowEscapeKey: false,
                didOpen: () => {
                    Swal.showLoading();
                },
            });
        },
        onSuccess: (_, variables) => {
            console.log(variables)
            Swal.fire({
                icon: 'success',
                text: 'Staff member removed successfully',
                showConfirmButton: false,
                timer: 5000,
                timerProgressBar: true,
            });

            // remove the assignment from the current data without refetching
            queryClient.setQueryData(queryKey, (oldData) => {
                return oldData.filter(staff => staff.staffAssignmentId !== variables.assignmentId);
            });
            
            // Invalidar las queries relacionadas para refrescar los datos
            queryClient.invalidateQueries({
                queryKey,
                refetchType: "none"
            });
        },
        onError: (error) => {
            Swal.fire({
                icon: 'error',
                text: error.response?.data?.message || 'An error occurred while removing the staff member.',
                showConfirmButton: false,
                timer: 5000,
                timerProgressBar: true,
            });

            console.error('Error removing staff member:', error?.message || 'Remove staff member failed');
        },
    })

    // handlers
    const handleFilterChange = () => {
		setShowInactiveOnly(!showInactiveOnly);
	};

    const handleSelectEntity = (entity) => {
        if (entity.clientID === entitySelected?._id) return;
        
        setEntitySelected({
            _id: entity.clientID,
            entityName: entity.clientName,
        })
    }

    // mutations handlers
    const massiveStaffUpload = ({ supplierId, entityId, file }) => {
        massiveStaffUploadMutation.mutate({ supplierId, entityId, file });
    }

    const createStaffHandler = async ({ supplierId, entityId, staffData }) => {
        try {
            await createStaffMutation.mutateAsync({ supplierId, entityId, staffData });
        } catch {
            return false
        }
    }

    const toggleStaffStatusHandler = ({ assignmentId }) => {
        toggleStaffStatusMutation.mutate({ assignmentId });
    }

    const removeStaffAssignmentHandler = ({ assignmentId }) => {
        removeStaffAssignmentMutation.mutate({ assignmentId });
    }

    const dataToReturn = {
        data,
        isError,
        error,
        refetch,

        // States
        showInactiveOnly,
        entitySelected,

        // Handlers
        handleFilterChange,
        handleSelectEntity,

        // Mutations
        massiveStaffUpload,
        createStaffHandler,
        removeStaffAssignmentHandler,
        toggleStaffStatusHandler
    }

    return dataToReturn;
};

export default useStaffList;
